<template>
  <v-app :dark="darkTheme">
    <v-snackbar
      v-if="message"
      :multi-line="!!message.body"
      :timeout="snackbarTimeout"
      top
      v-model="snackbarVisible"
      :color="getMessageColor(message)"
    >
      <div class="clickable" @click="snackbarClicked">
        <div>
          <b>{{ message.subject }}</b>
        </div>
        <div>{{ message.body }}</div>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          outlined
          color="white"
          v-bind="attrs"
          @click.stop="snackbarVisible = false"
          >{{ $vuetify.lang.t("$vuetify.crm.default.button.close") }}</v-btn
        >
      </template>
    </v-snackbar>

    <v-navigation-drawer
      v-model="menuDrawer"
      :mini-variant="menuMinimized"
      app
      permanent
      dark
      color="primary darken-4"
    >
      <v-list dense nav class="py-0" role="navigation" color="primary darken-3">
        <v-list-item
          two-line
          class="px-0"
          :ripple="false"
          title="Öppna/stäng menyn"
          @click="menuMinimized = !menuMinimized"
        >
          <v-list-item-avatar>
            <v-img v-if="logo" :src="logo" max-width="36" max-height="36" />
            <v-app-bar-nav-icon v-else class="white--text"></v-app-bar-nav-icon>
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title v-text="title"></v-list-item-title>
            <v-list-item-subtitle>{{ account.name }}</v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action>
            <v-icon>mdi-chevron-left</v-icon>
          </v-list-item-action>
        </v-list-item>

        <v-divider></v-divider>

        <v-list-item
          v-for="item in menuItems"
          :key="item.path"
          :title="$vuetify.lang.t('$vuetify.' + item.meta.title)"
          @click.stop="
            $router
              .push({ name: item.name, params: { tenant: $crm.tenant } })
              .catch((err) => {})
          "
        >
          <v-list-item-action>
            <v-icon v-text="item.meta.icon"></v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{
              $vuetify.lang.t("$vuetify." + item.meta.title)
            }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-divider></v-divider>

        <!--
        <v-list-item link @click="$eventBus.$emit('logout')">
          <v-list-item-icon>
            <v-icon>logout</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title>{{$vuetify.lang.t('$vuetify.logout')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        -->
      </v-list>

      <v-divider></v-divider>

      <v-list color="primary darken-4">
        <v-list-item
          v-for="(item, i) in history"
          :key="i"
          :style="'opacity:' + Math.max(1, 9 - i) / 10"
          @click.stop="showHistory(item)"
        >
          <v-list-item-action>
            <v-icon v-text="item.icon"></v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{ item.text }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar app v-if="isTenantActive">
      <v-progress-linear
        v-show="$store.getters.isLoading"
        :indeterminate="true"
        color="info"
        absolute
        bottom
      ></v-progress-linear>

      <v-breadcrumbs v-if="$vuetify.breakpoint.smAndUp" divider="→">
        <v-breadcrumbs-item
          v-for="item in breadcrumbs"
          :to="item.to"
          :exact="true"
          :key="item.text"
          :disabled="item.disabled"
        >
          {{ item.text }}
        </v-breadcrumbs-item>
      </v-breadcrumbs>

      <v-spacer></v-spacer>

      <!--
      <v-text-field
        v-model="search"
        hide-details
        prepend-inner-icon="search"
        single-line
        @change="globalSearch"
      ></v-text-field>
      -->

      <v-badge
        color="red"
        offset-x="16"
        offset-y="18"
        :value="unreadNotifications > 0"
      >
        <template v-slot:badge>
          <span>{{ unreadNotifications }}</span>
        </template>
        <v-btn icon @click="notificationsVisible = !notificationsVisible"
          ><v-icon>mdi-email</v-icon></v-btn
        >
      </v-badge>

      <v-btn
        v-if="$crm.tenant && isAdmin"
        icon
        exact
        :to="{ name: 'admin', params: { tenant: $crm.tenant } }"
        class="me-1"
      >
        <v-icon>mdi-cogs</v-icon>
      </v-btn>

      <v-menu v-if="availableAccounts.length > 1">
        <template v-slot:activator="{ on }">
          <v-btn icon v-on="on" class="mr-2">
            <v-icon>mdi-bank</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item
            v-for="item in availableAccounts"
            :key="item.id"
            @click="selectAccount(item)"
            :disabled="item.id === currentAccountId"
          >
            <v-list-item-icon>
              <img
                v-if="item.tenant.icon"
                :src="`/${item.tenant.id}/v1/content/assets/${item.tenant.icon}`"
                height="36"
                width="36"
                alt="Logo"
              />
              <v-icon v-else>$company</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ item.tenant.name }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-divider></v-divider>

          <v-list-item :to="{ name: 'tenant-switch' }">
            <v-list-item-icon>
              <v-icon>$company</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>
                {{ $vuetify.lang.t("$vuetify.avtala.app.accounts.label") }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-badge
        class="mr-2"
        dot
        color="green"
        offset-x="3"
        offset-y="6"
        :value="isSocketConnected"
      >
        <CrmAvatar
          :value="account"
          :size="36"
          class="clickable"
          @click="
            $router.push({ name: 'settings', params: { tenant: $crm.tenant } })
          "
        ></CrmAvatar>
      </v-badge>

      <v-btn icon @click.stop="$store.dispatch('toggleRightDrawer')">
        <v-icon>menu</v-icon>
      </v-btn>
    </v-app-bar>

    <v-app-bar app clipped-left v-else>
      <v-toolbar-title v-text="title" class="ml-3"></v-toolbar-title>
      <v-spacer></v-spacer>

      <span class="mr-3 clickable hidden-xs-only">
        {{ $store.getters["account/user"].name }}
      </span>

      <v-btn icon @click="logout" title="Logga ut">
        <v-icon>$logout</v-icon>
      </v-btn>
    </v-app-bar>

    <v-main>
      <router-view />
    </v-main>

    <v-navigation-drawer
      temporary
      :right="true"
      :width="480"
      :value="rightDrawer"
      fixed
      app
      @input="rightDrawerStatus($event)"
    >
      <router-view name="sidebar"></router-view>
    </v-navigation-drawer>

    <CrmNotificationList
      :visible="notificationsVisible"
      :items="notifications"
      @read="toggleRead"
      @remove="removeNotification"
      @reset="removeAllNotifications"
      @close="closeNotifications"
    />

    <AppFooter>
      <v-spacer></v-spacer>
      <v-speed-dial v-model="fab" direction="top" class="mr-15">
        <template v-slot:activator>
          <v-btn v-model="fab" icon small>
            <v-icon v-if="fab">$close</v-icon>
            <v-icon v-else>{{ currentLocale.icon }}</v-icon>
          </v-btn>
        </template>
        <v-btn fab small @click="changeLocale('sv')">
          <v-icon>$swedish</v-icon>
        </v-btn>
        <v-btn fab small @click="changeLocale('fi')" v-if="false">
          <v-icon>$finnish</v-icon>
        </v-btn>
        <v-btn fab small @click="changeLocale('en')">
          <v-icon>$english</v-icon>
        </v-btn>
      </v-speed-dial>
    </AppFooter>

    <v-btn
      v-if="$crm.tenant"
      :to="{
        name: 'help',
        params: { tenant: $crm.tenant },
        query: { referer: $route.name },
      }"
      color="warning"
      dark
      small
      bottom
      right
      fixed
      fab
    >
      <span class="headline">?</span>
    </v-btn>

    <MapDialog
      :visible="mapVisible"
      :home="homeAddress"
      :title="mapTitle"
      :address="mapAddress"
      :position="mapPosition"
      @close="showMap({})"
    ></MapDialog>

    <ConfirmLogoutDialog
      v-model="logoutDialog"
      @logout="logout(false)"
    ></ConfirmLogoutDialog>
  </v-app>
</template>

<script>
/* eslint-disable no-console, no-unused-vars */
import moment from "moment";
import CrmNotificationList from "./components/CrmNotificationList";
import AppFooter from "./components/AppFooter";
import MapDialog from "@/components/MapDialog";
import CrmAvatar from "@/components/CrmAvatar";
import ConfirmLogoutDialog from "@/components/ConfirmLogoutDialog.vue";

export default {
  name: "App",
  components: {
    ConfirmLogoutDialog,
    CrmAvatar,
    MapDialog,
    CrmNotificationList,
    AppFooter,
  },
  data() {
    return {
      fab: null,
      locales: ["sv", "en"],
      menuDrawer: true,
      menuMinimized: this.$vuetify.breakpoint.mobile,
      snackbarVisible: false,
      snackbarTimeout: 10000,
      notificationsVisible: false,
      mapVisible: false,
      mapTitle: null,
      mapAddress: null,
      mapPosition: null,
      search: null,
      logoutDialog: false,
    };
  },
  computed: {
    currentLocale() {
      const lang = this.$vuetify.lang.current || "sv";
      const map = {
        en: "$english",
        sv: "$swedish",
        fi: "$finnish",
      };
      return { icon: map[lang] };
    },
    availableAccounts() {
      return this.$store.getters["account/accounts"];
    },
    currentAccountId() {
      return this.$store.getters["account/account"].id;
    },
    title() {
      if (this.isTenantActive) {
        return this.$store.getters["account/tenantName"];
      }
      return "";
    },
    isAdmin() {
      return this.$store.getters["account/isAdmin"];
    },
    isAuthenticated() {
      return this.$store.getters["account/isAuthenticated"];
    },
    isTenantActive() {
      return !!this.$crm.tenant;
    },
    breadcrumbs() {
      return this.$store.getters.breadcrumbs;
    },
    history() {
      return this.$store.getters.history;
    },
    rightDrawer() {
      return this.$store.getters.rightDrawer;
    },
    menuItems() {
      if (!this.isTenantActive) {
        return [];
      }
      return this.$router.options.routes.filter((route) => {
        if (!route.meta) {
          return false;
        }
        if (!route.meta.menu) {
          return false;
        }
        if (
          route.meta.feature &&
          !this.$store.getters.feature(route.meta.feature)
        ) {
          return false;
        }
        if (
          route.meta.permission &&
          !this.$store.getters["account/permission"](route.meta.permission)
        ) {
          return false;
        }
        return true;
      });
    },
    message() {
      return this.$store.getters.message;
    },
    user() {
      return this.$store.getters["account/user"];
    },
    account() {
      return this.$store.getters["account/account"];
    },
    homeAddress() {
      let a = this.$store.getters["account/userSetting"]("homeAddress");
      if (a) {
        return a;
      }
      a = this.$store.getters["account/tenant"]?.address;
      if (a) {
        return a.value;
      }
      return null;
    },
    darkTheme() {
      return localStorage.getItem("theme.dark") === "true";
    },
    avatar() {
      const tenant = this.$crm.tenant;
      const account = this.$store.getters["account/account"];
      if (tenant && account?.picture) {
        return this.$crm.getContentURL(account.picture, "thumbnail");
      }
      return null;
    },
    logo() {
      const tenant = this.$store.getters["account/tenant"];
      if (tenant?.icon) {
        return this.$crm.getContentURL(tenant.icon, "thumbnail");
      }
      return null;
    },
    notifications() {
      return (this.$store.getters.notifications || []).filter((n) => !n.action);
    },
    unreadNotifications() {
      return this.notifications.filter((n) => n.read === false).length;
    },
    isSocketConnected() {
      return this.$store.getters.isSocketConnected;
    },
    isSocketError() {
      return this.$store.getters.isSocketError;
    },
  },
  methods: {
    changeLocale(locale) {
      this.$vuetify.lang.current = locale;
    },
    selectAccount(item) {
      this.$router.replace("/" + item.tenant.id + "/dashboard");
      /*this.$store.dispatch('account/selectAccount', item.id)
          .then(account => this.$router.push({ name: 'home', params: { tenant: account.tenant.id } }))*/
    },
    goto(item) {
      this.$router.push({
        name: item.name,
        params: { tenant: this.$crm.tenant },
      });
    },
    rightDrawerStatus(event) {
      this.$store.commit("setRightDrawer", event);
    },
    showHistory(item) {
      this.rightDrawerStatus(false);
      setTimeout(() => this.$router.push(item.to), 350);
    },
    toggleRead(item) {
      /*if (!item.read) {
          this.$store.dispatch('toggleRead', item)
        }*/
      this.handleAttachments(item);
    },
    closeNotifications() {
      this.notificationsVisible = false;
    },
    removeNotification(item) {
      this.$store.dispatch("deleteNotification", item);
    },
    removeAllNotifications() {
      this.$store.dispatch("deleteAllNotifications");
    },
    snackbarClicked() {
      this.handleAttachments(this.message);
    },
    parseTimeout(timeout) {
      if (timeout) {
        if (timeout === "short") {
          return 2000;
        } else if (timeout === "normal" || timeout === "medium") {
          return 5000;
        } else if (timeout === "long") {
          return 10000;
        } else {
          return Math.max(timeout, 2000);
        }
      }
      return 5000;
    },
    handleAttachments(notification) {
      if (notification.attachments && notification.attachments.length) {
        this.openContent(notification.attachments[0]);
      } else if (notification.attributes?.route) {
        this.$router.push(notification.attributes?.route);
      }
    },
    openContent(path) {
      return this.$store
        .dispatch("content/getContentMetadata", path)
        .then((content) =>
          this.$store
            .dispatch("content/downloadContent", content.id)
            .then((blob) => {
              const data = window.URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = data;
              link.target = "_blank";
              link.download = content.filename; // Trick to make filename appear in browser window
              link.click();
              setTimeout(function () {
                // For Firefox it is necessary to delay revoking the ObjectURL
                window.URL.revokeObjectURL(data);
              }, 250);
            })
        );
    },
    getPathAsContentItem(path) {
      return { path: path, filename: path.split("/").pop() };
    },
    handleError({ message, error }) {
      const text = this.$vuetify.lang.t("$vuetify." + message);
      this.$store.dispatch("message", {
        subject: text,
        body: error,
        color: "error darken-1",
        timeout: 10000,
      });
    },
    getMessageColor(message) {
      if (message.color) {
        return message.color;
      }
      const category = message.category?.toLowerCase();
      if (category === "reminder") {
        return "info darken-1";
      }
      if (category === "error") {
        return "error darken-1";
      }
      return "success darken-2";
    },
    onActivityCreated({ activity }) {
      this.$router.push({
        name: "calendar",
        params: {
          tenant: this.$crm.tenant,
          view: "week",
          date: moment(activity.startTime).format("YYYY-MM-DD"),
        },
      });
    },
    onContactCreated({ contact }) {
      this.$store.commit("contract/setCreatedContact", contact);
    },
    showMap({ address, position }) {
      if (address) {
        this.mapTitle = null;
        this.mapPosition = null;
        this.mapAddress = address;
        this.mapVisible = true;
      } else if (position) {
        this.mapTitle = position.title;
        this.mapAddress = null;
        this.mapPosition = position;
        this.mapVisible = true;
      } else {
        this.mapTitle = null;
        this.mapAddress = null;
        this.mapPosition = null;
        this.mapVisible = false;
      }
    },
    globalSearch(term) {
      this.$store.commit("contract/setContractSearch", { docs: term });
      this.$router.push({
        name: "list-contract",
        params: { tenant: this.$crm.tenant },
      });
    },
    logout(args) {
      if (args && !document.hidden) {
        this.logoutDialog = true;
      } else {
        this.logoutDialog = false;
        const appUrl = process.env.VUE_APP_API + process.env.VUE_APP_BASE;
        localStorage.removeItem("tenant");
        this.$keycloak.logout({ redirectUri: appUrl });
      }
    },
  },
  created() {
    this.$eventBus.$on("error", this.handleError);
    this.$eventBus.$on("activity:created", this.onActivityCreated);
    this.$eventBus.$on("crm-contact-created", this.onContactCreated);
    this.$eventBus.$on("map:show", this.showMap);
    this.$eventBus.$on("logout", this.logout);
  },
  beforeDestroy() {
    this.$eventBus.$off("logout", this.logout);
    this.$eventBus.$off("map:show", this.showMap);
    this.$eventBus.$off("crm-contact-created", this.onContactCreated);
    this.$eventBus.$off("activity:created", this.onActivityCreated);
    this.$eventBus.$off("error", this.handleError);
  },
  watch: {
    message: function (oldMsg, newMsg) {
      const timeout = this.parseTimeout(newMsg?.timeout);
      // Snackbar timer is reset only when timout is changed,
      // therefore we add a random number between 0-100 to force it to change every time.
      // Without this (workaround) a new message that arrives 2 seconds before the previous message would timeout will only be visible for 2 seconds.
      this.snackbarTimeout = timeout + Math.floor(Math.random() * 100);
      this.snackbarVisible = true; //oldSubject !== newSubject
      this.$store.dispatch("fetchNotifications"); // refresh notification list
    },
  },
};
</script>

<style>
.v-date-picker-table {
  height: 100%;
  max-height: 300px;
}
.theme--light.v-card > .v-card__text {
  color: rgba(0, 0, 0, 0.8) !important;
}
.clickable {
  cursor: pointer;
}
.inputfile {
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;
}
.archived {
  text-decoration: line-through;
}
.system {
  color: darkred;
}
</style>

<style scoped>
.v-progress-linear {
  margin: 0;
}
</style>
